<!--
  AppNavbar.vue
  ============================
  【功能簡介】
  此元件為「動物疫情資訊回應平台」的頂部導覽列：
  - 左側顯示平台名稱，點擊可返回首頁
  - 右側在大螢幕顯示選單連結，在小螢幕顯示下拉選單
  - 使用 localStorage 判斷是否為管理員並切換選單
  - 根據當前路由自動判斷選單項目的 active 狀態
  - 整合 ProfileDropdown.vue 使用者操作選單
  - （新增）顯示全站瀏覽次數（每分頁首次載入才會計數）
-->

<template>
  <VaNavbar class="py-2 px-0" color="#40826D">
    <template #left>
      <div class="left">
        <RouterLink to="/" aria-label="Visit home page" class="h-12 flex items-center">
          <h1 class="lg:ml-5 text-lg sm:text-2xl lg:text-3xl font-medium text-white">動物疫情資訊回應平台</h1>
        </RouterLink>
      </div>
    </template>
    <template #right>
      <div class="right flex items-center lg:space-x-6">
        <!-- For larger screens -->
        <div class="hidden lg:flex space-x-6">
          <RouterLink
            v-for="(topic, index) in admin ? topics : topics2"
            :key="index"
            :to="topic.to"
            :class="['text-xl text-white hover:text-yellow-300 transition', isActive(topic.to) ? 'active-link' : '']"
          >
            {{ topic.title }}
          </RouterLink>
        </div>

        <!-- For smaller screens -->
        <VaDropdown class="lg:hidden">
          <template #anchor>
            <VaButton preset="secondary" icon="fas fa-bars" color="#FFFFFF" @click="toggleMenu" />
          </template>
          <VaDropdownContent class="w-44 px-0 py-4" :style="{ '--hover-color': 'rgba(64, 130, 109, 0.1)' }">
            <RouterLink
              v-for="(topic, index) in admin ? topics : topics2"
              :key="index"
              :to="topic.to"
              class="block py-2 text-center text-lg text-gray-700 hover-setting"
              @click="menuOpen = false"
            >
              {{ topic.title }}
            </RouterLink>
          </VaDropdownContent>
        </VaDropdown>

        <!-- NEW: 瀏覽次數顯示（不影響既有結構） -->
        <span
          v-if="viewCount !== null"
          class="views-counter inline-flex items-center text-white text-base lg:text-lg"
          aria-live="polite"
          title="全站瀏覽次數"
        >
          <i class="fas fa-eye mr-2" aria-hidden="true"></i>
          {{ viewCount.toLocaleString() }}
        </span>

        <ProfileDropdown />
      </div>
    </template>
  </VaNavbar>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount } from 'vue'
import ProfileDropdown from './ProfileDropdown.vue'
import { useRoute } from 'vue-router'

const route = useRoute()

const topics = [
  {
    title: '監測資訊',
    to: '/chat',
  },
  {
    title: '國際疫情',
    to: '/global-outbreak',
  },
  {
    title: '文獻搜尋',
    to: '/literature-search',
  },
  {
    title: '新聞搜尋',
    to: '/news-search',
  },
  // {
  //   title: '序列分析',
  //   to: '/virus-sequence',
  // },
  {
    title: '語音紀錄',
    to: '/audio-to-text',
  },
]
const topics2 = [
  {
    title: '監測資訊',
    to: '/chat',
  },
  {
    title: '文獻搜尋',
    to: '/literature-search',
  },
  {
    title: '新聞搜尋',
    to: '/news-search',
  },
  // {
  //   title: '序列分析',
  //   to: '/virus-sequence',
  // },
  {
    title: '語音紀錄',
    to: '/audio-to-text',
  },
]

const isActive = (to: string) => {
  if (to === '/literature-search') {
    return route.path.startsWith('/literature-search')
  }
  if (to === '/news-search') {
    return route.path.startsWith('/news-search')
  }
  return route.path === to
}

const menuOpen = ref(false)
const toggleMenu = () => {
  menuOpen.value = !menuOpen.value
}

const admin = ref(false)
onMounted(async () => {
  admin.value = localStorage.getItem('admin') === 'yes'
})

// NEW -------------------------------
const viewCount = ref<number | null>(null)

async function fetchViewCount() {
  try {
    const res = await fetch('/view-count/views', { credentials: 'same-origin' })
    if (!res.ok) throw new Error(`GET /api/views failed: ${res.status}`)
    const data = (await res.json()) as { count: number }
    viewCount.value = data.count ?? 0
  } catch {}
}

async function incrementViewOncePerTab() {
  const key = 'site_view_incremented_once_per_tab'
  if (sessionStorage.getItem(key)) return
  try {
    const res = await fetch('/view-count/views//increment', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ reason: 'navbar-first-load' }),
    })
    if (!res.ok) throw new Error(`POST /api/views/increment failed: ${res.status}`)
    sessionStorage.setItem(key, '1')
  } catch {}
}

// ✅ 少了這個大括號，補上
function handleViewCountRefresh() {
  fetchViewCount()
}

onMounted(async () => {
  await incrementViewOncePerTab()
  await fetchViewCount()
  window.addEventListener('view-count-refresh', handleViewCountRefresh)
})

onBeforeUnmount(() => {
  window.removeEventListener('view-count-refresh', handleViewCountRefresh)
})
// NEW -------------------------------
</script>

<style lang="scss" scoped>
.va-navbar {
  z-index: 2;

  @media screen and (max-width: 950px) {
    .left {
      width: 100%;
    }

    .app-navbar__actions {
      display: flex;
      justify-content: space-between;
    }
  }
}

.left {
  display: flex;
  align-items: center;
  margin-left: 1rem;

  & > * {
    margin-right: 1rem;
  }

  & > *:last-child {
    margin-right: 0;
  }
}

.right {
  display: flex;
  align-items: center;

  & > * {
    margin-left: 1rem;
  }

  & > *:first-child {
    margin-left: 0;
  }
}

.active-link {
  font-weight: bold;
  color: #ffd700;
}

.exact-active-link {
  font-weight: bold;
  color: #ffd700;
}

@media (min-width: 1230px) {
  .lg\\:hidden {
    display: none;
  }
}

@media (max-width: 1229px) {
  .lg\\:flex {
    display: none;
  }
}

.hover-setting:hover {
  background-color: rgba(64, 130, 109, 0.1);
}

.va-navbar a {
  text-decoration: none;
}

.va-navbar a:hover,
.va-navbar a:focus {
  text-decoration: none;
}

/* NEW: 輕量樣式，不影響原本排版 */
.views-counter {
  padding: 0.25rem 0.5rem;
  border: 1px solid rgba(255, 255, 255, 0.35);
  border-radius: 9999px;
  line-height: 1;
  white-space: nowrap;
}
</style>
